import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, from, BehaviorSubject } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import firebase from 'firebase/app';
import { AngularFireAuth } from '@angular/fire/auth';
import { AngularFirestore } from '@angular/fire/firestore';
import { AngularFireFunctions } from '@angular/fire/functions';

import {
  NotifyService,
  NotifyAlertService,
  ConfirmService,
  Message,
} from '@monorepo/ui';
import { AuthUser } from '../models';
// import { AuthUser } from '@monorepo/ui/auth';
import { WindowService } from '@monorepo/ui';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  user: Observable<AuthUser | null>;
  token: BehaviorSubject<string> = new BehaviorSubject<string>('');
  isAdmin: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isEmailverified: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  isUserLoggedIn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  windowRef: BehaviorSubject<any> = new BehaviorSubject<any>({});
  constructor(
    private afAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private router: Router,
    private notify: NotifyService,
    private notifyAlertService: NotifyAlertService,
    private confirmPopup: ConfirmService,
    private fun: AngularFireFunctions,
    private win: WindowService
  ) {
    this.windowRef.next(this.win.windowRef);
    this.user = this.afAuth.authState.pipe(
      switchMap((user) => {
        if (user) {
          this.isUserLoggedIn.next(true);
          this.isEmailverified.next(user.emailVerified);
          this.getToken(user);
          return this.afs
            .doc<AuthUser>(`users/${user.uid}`)
            .valueChanges()
            .pipe(
              tap((userAction) => {
                if (userAction && userAction.role === 'ADMIN') {
                  this.isAdmin.next(true);
                }
              })
            );
        } else {
          this.isUserLoggedIn.next(false);
          return of(null);
        }
      })
    );
  }

  //Token
  public async getToken(user: firebase.User) {
    const token = await user.getIdToken();
    this.token.next(token);
  }

  ////// OAuth Methods /////
  public checkIsAdmin(): boolean {
    return this.isAdmin.value;
  }

  public checkIsEmailVerified(): boolean {
    return this.isEmailverified.value;
  }

  public googleLogin(): Promise<any> {
    const provider = new firebase.auth.GoogleAuthProvider();
    return this.oAuthLogin(provider);
  }

  public githubLogin(): Promise<any> {
    const provider = new firebase.auth.GithubAuthProvider();
    return this.oAuthLogin(provider);
  }

  public facebookLogin(): Promise<any> {
    const provider = new firebase.auth.FacebookAuthProvider();
    return this.oAuthLogin(provider);
  }

  public twitterLogin(): Promise<any> {
    const provider = new firebase.auth.TwitterAuthProvider();
    return this.oAuthLogin(provider);
  }

  private oAuthLogin(provider: any): Promise<any> {
    return this.afAuth
      .signInWithPopup(provider)
      .then((credential) => {
        this.notify.update('Welcome to Firestarter!!!', 'success');
        return this.updateUserData(credential.user);
      })
      .catch((error) => {
        this.handleError(error);
        return error;
      });
  }

  //// Anonymous Auth ////

  public anonymousLogin(): Promise<any> {
    return this.afAuth
      .signInAnonymously()
      .then((credential) => {
        this.updateUserData(credential.user); // if using firestore
        return credential.user;
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  //// Email/Password Auth ////

  public async emailSignUp(userPayload: any): Promise<any> {
    const userCredential: firebase.auth.UserCredential =
      await this.afAuth.createUserWithEmailAndPassword(
        userPayload.email,
        userPayload.password
      );
    this.updateUserData(userCredential.user, userPayload);
    return userCredential.user;
  }

  public emailLogin(
    email: string,
    password: string,
    remember?: boolean
  ): Observable<any> {
    return from(
      this.afAuth
        .setPersistence(
          remember
            ? firebase.auth.Auth.Persistence.LOCAL
            : firebase.auth.Auth.Persistence.SESSION
        )
        .then(() => {
          return this.afAuth.signInWithEmailAndPassword(email, password);
          // .pipe(
          //   tap((credential) => {
          //     console.log(credential);
          //     if (credential && !credential.user.emailVerified) {
          //       this.notifyAlertService.info(Message.Email_Not_Verified);
          //     }
          //   }),
          //   catchError((err, caught) => {
          //     console.log(err, caught);
          //     this.notifyAlertService.info(err.message);
          //     return err;
          //   })
          // );
        })
        .catch((err) => {
          // this.notifyAlertService.info(err.message);
          throw err;
        })
    );
  }

  // Sends email allowing user to reset password
  public resetPassword(email: string, resetUrl: string): Observable<any> {
    const setting = {
      url: resetUrl,
    };
    return from(
      this.afAuth
        .sendPasswordResetEmail(email, setting)
        .then((response) => {
          return response;
        })
        .catch((error) => {
          this.handleError(error);
        })
    );
  }

  public confirmReserPassword(code: string, password: string): Observable<any> {
    return from(this.afAuth.confirmPasswordReset(code, password));
  }

  public signOut(returnURL?: string): void {
    this.afAuth.signOut().then(() => {
      this.isUserLoggedIn.next(false);
      returnURL
        ? this.router.navigateByUrl(returnURL)
        : this.router.navigateByUrl('/');
    });
  }

  // If error, console log and notify user
  private handleError(error: Error): void {
    this.notify.update(error.message, 'error');
  }

  // Sets user data to firestore after succesful login
  public updateUserData(
    userInfo?: firebase.User,
    userPayload?: any
  ): Promise<any> {
    const authUser: AuthUser = {
      uid: userInfo?.uid,
      fullName:
        userPayload?.displayName ||
        userInfo?.displayName ||
        userPayload?.firstName + ' ' + userPayload?.lastName ||
        'Guest',
      displayName:
        userPayload?.displayName ||
        userInfo?.displayName ||
        userPayload?.firstName + ' ' + userPayload?.lastName ||
        'Guest',
      email: userPayload?.email || userInfo?.email,
      phoneNumber: userPayload?.phoneNumber || userInfo?.phoneNumber,
      photoURL:
        userPayload?.photoURL ||
        userInfo?.photoURL ||
        'https://fomantic-ui.com/images/avatar/small/matt.jpg',
      country: userPayload?.country || null,
      isAdmin: false,
      role: 'USER',
      isMobileVerified: userPayload?.isMobileVerified || false,
      isEmailVerified: userPayload?.isEmailVerified || false,
      countryCode: userPayload?.countryCode || null,
      userId: userPayload?.userId || null,
    };
    const docRef = this.afs.collection(`users`).doc(authUser.uid).get();
    return docRef.toPromise().then((userExists): void => {
      if (!userExists.exists) {
        this.afs
          .collection(`users`)
          .doc(authUser.uid)
          .set(authUser, { merge: true });
      } else {
        delete authUser.role;
        delete authUser.isEmailVerified;
        delete authUser.isMobileVerified;
        delete authUser.isAdmin;
        this.afs
          .collection(`users`)
          .doc(authUser.uid)
          .update({ ...authUser });
      }
    });
  }

  public sendVerificationMail(): Promise<any> {
    return firebase.auth().currentUser.sendEmailVerification();
  }

  public verifyPhoneNumber(phone: string, appVerifier): Observable<any> {
    return from(this.afAuth.signInWithPhoneNumber(phone, appVerifier));
  }

  public sendLoginCode(number, recaptcha): Observable<any> {
    if (!number) {
      return from(null);
    }
    const appVerifier = recaptcha;
    const provider = new firebase.auth.PhoneAuthProvider();
    return from(provider.verifyPhoneNumber(number, appVerifier));
  }

  verifyLoginCode(verificationId, verificationCode): Observable<any> {
    const phoneCredentials = firebase.auth.PhoneAuthProvider.credential(
      verificationId,
      verificationCode
    );
    return from(
      firebase.auth().currentUser.updatePhoneNumber(phoneCredentials)
    );
  }

  verifySignInCode(confirmationResult, verificationCode): Observable<any> {
    return from(confirmationResult.confirm(verificationCode));
  }

  public phoneLogin(verificationId, verificationCode): Observable<any> {
    const phoneCredentials = firebase.auth.PhoneAuthProvider.credential(
      verificationId,
      verificationCode
    );
    return from(firebase.auth().signInWithCredential(phoneCredentials));
  }

  public handleVerificaitonEmail(code: string, url: string): void {
    this.afAuth
      .applyActionCode(code)
      .then((): void => {
        this.confirmPopup
          .forceConfirmModal(
            'Email Verified',
            Message.Email_Verified_Success,
            'Tiny'
          )
          .onApprove((): void => {
            this.router.navigateByUrl(url);
          });
      })
      .catch((error): void => {
        this.notifyAlertService.error(error.message);
        this.router.navigateByUrl(url);
      });
  }

  public handleSigngazeVerificationMail(code: string, url: string): void {
    this.user.subscribe({
      next: (user): void => {
        this.confirmPopup
          .forceConfirmModal(
            'Email Verified',
            Message.Email_Verified_Success,
            'Tiny'
          )
          .onApprove((): void => {
            this.afAuth
              .applyActionCode(code)
              .then((): void => {
                const sendWelcomeEmail =
                  this.fun.httpsCallable('sendWelcomeEmail');
                sendWelcomeEmail(user).subscribe();
                this.router.navigateByUrl(url);
              })
              .catch((error): void => {
                this.notifyAlertService.error(error.message);
                this.router.navigateByUrl(url);
              });
          });
      },
    });
  }

  public checkEmailAlreadyExists(email: string): void {
    this.afs
      .collection('users')
      .ref.where('state', '==', email)
      .get()
      .then(() => {})
      .catch(() => {});
  }

  public getCountries(): Observable<any[]> {
    return this.afs.collection('countries').valueChanges();
  }
}
